import React, { PropsWithChildren } from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useInvoiceIssuesQuery } from 'queries/invoiceIssues'

type InvoiceIssuesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useInvoiceIssuesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
  }
  actions: {
    refetch: () => void
  }
}

const [Context, useInvoiceIssuesContextValue] =
  createContext<InvoiceIssuesContextState>({
    name: 'InvoiceIssuesContext',
  })

export const InvoiceIssuesProvider = ({ children }: PropsWithChildren) => {
  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useInvoiceIssuesQuery()

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value: InvoiceIssuesContextState = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
    },
    actions: {
      refetch,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useInvoiceIssuesContextValue }
